import React, { useState, useMemo, useEffect, useRef, useCallback, startTransition } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import './OperationalMap.css';

// ── Window size ──────────────────────────────────────────────────────────────
function useWindowSize() {
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    useEffect(() => {
        let timer;
        const handleResize = () => { clearTimeout(timer); timer = setTimeout(() => setWindowSize({ width: window.innerWidth, height: window.innerHeight }), 150); };
        window.addEventListener('resize', handleResize);
        return () => { window.removeEventListener('resize', handleResize); clearTimeout(timer); };
    }, []);
    return windowSize;
}

// ── SVG icons ────────────────────────────────────────────────────────────────
const ICONS = {
    home: <svg viewBox="0 0 24 24"><path fill="currentColor" d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5z" /></svg>,
    location: <svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" /></svg>,
    layers: <svg viewBox="0 0 24 24"><path fill="currentColor" d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z" /></svg>,
    search: <svg viewBox="0 0 24 24"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5A6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14z" /></svg>,
    filter: <svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 4h18v2.01L17 12v6.99l-4 2v-8.99L7 6.01V4z" /></svg>,
    list: <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor"><path d="M0 0h24v24H0V0z" fill="none" /><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" /></svg>,
    close: <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor"><path d="M0 0h24v24H0V0z" fill="none" /><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" /></svg>,
};

// ── Google Maps marker icons ──────────────────────────────────────────────────
// Gota com buraco circular central — mesmo formato do pin padrão do Google Maps
const PIN_PATH = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
const ICON_CONFIG = {
    critico:                { fill: '#D32F2F', stroke: '#FFFFFF' }, // Vermelho   — T. cruzi Positivo
    alerta:                 { fill: '#FFA000', stroke: '#FFFFFF' }, // Laranja    — alerta genérico
    monitorado_com_captura: { fill: '#1976D2', stroke: '#FFFFFF' }, // Azul       — capturado, lab pendente
    negativo:               { fill: '#388E3C', stroke: '#FFFFFF' }, // Verde      — T. cruzi Negativo (lab)
    vestigios:              { fill: '#FBC02D', stroke: '#FFFFFF' }, // Amarelo    — Vestígios presentes
    sem_captura:            { fill: '#546E7A', stroke: '#FFFFFF' }, // Azul-cinza — Pesquisado sem captura
    denuncia_pendente:      { fill: '#FFA000', stroke: '#FFFFFF' }, // Laranja    — Notificação pendente
    denuncia_atendida:      { fill: '#00897B', stroke: '#FFFFFF' }, // Teal       — Notificação atendida
    denuncia_recusada:      { fill: '#7B1FA2', stroke: '#FFFFFF' }, // Roxo       — Notificação improcedente
};
const _iconCache = new Map();
// Symbol icons (path) nunca são otimizados pelo Maps — cada um vira um DOM element.
// PNG via canvas permite otimização em canvas layer, muito mais rápido no pan/zoom.
const getGoogleIcon = (id) => {
    if (!window.google?.maps) return null;
    if (_iconCache.has(id)) return _iconCache.get(id);
    const cfg = ICON_CONFIG[id] || ICON_CONFIG.sem_captura;
    const SIZE = 38;
    const SCALE = SIZE / 24;
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    ctx.scale(SCALE, SCALE);
    const p = new Path2D(PIN_PATH);
    ctx.fillStyle = cfg.fill;
    ctx.fill(p);
    ctx.strokeStyle = cfg.stroke;
    ctx.lineWidth = 1.5;
    ctx.stroke(p);
    const icon = {
        url: canvas.toDataURL(),
        scaledSize: new window.google.maps.Size(SIZE, SIZE),
        anchor: new window.google.maps.Point(Math.round(12 * SCALE), Math.round(22 * SCALE)),
    };
    _iconCache.set(id, icon);
    return icon;
};

// ── Classification ────────────────────────────────────────────────────────────
const getClassificacaoFoco = (item) => {
    const { ocorrencia, exame } = item.dados;

    if (!ocorrencia) {
        const status = item.dados.denuncia?.status;
        if (status === 'atendida' || status === 'resolvida') return { id: 'denuncia_atendida', cor: '#00897B', texto: 'Notificação Atendida' };
        if (status === 'recusada') return { id: 'denuncia_recusada', cor: '#7B1FA2', texto: 'Notificação Improcedente' };
        return { id: 'denuncia_pendente', cor: '#FFA000', texto: 'Notificação Pendente' };
    }

    const exemplares = exame?.exames_exemplares || [];
    const temCaptura = (Number(ocorrencia.barbeiros_intradomicilio) || 0) + (Number(ocorrencia.barbeiros_peridomicilio) || 0) > 0;
    const laudoFinalizado = exame && exemplares.length > 0 && exemplares.every(ex => ex.resultado && ex.resultado !== 'Não Examinado');
    const isInfectado = exemplares.some(ex => ex.resultado?.toLowerCase().includes('positivo'));
    const temNinfaIntra = exemplares.some(ex => ex.estagio?.toLowerCase().includes('ninfa') && ex.captura?.toLowerCase().includes('intra'));
    const normalizeVestigio = (v) => v ? v.toLowerCase().trim() : '';
    const isVazio = (v) => !v || v === '' || v === 'nenhum' || v === 'sem vestigios';
    const temVestigios = !isVazio(normalizeVestigio(ocorrencia.vestigios_intradomicilio)) || !isVazio(normalizeVestigio(ocorrencia.vestigios_peridomicilio));

    if (isInfectado || temNinfaIntra) return { id: 'critico', cor: '#D32F2F', texto: 'T. cruzi Positivo' };
    if (temCaptura && !laudoFinalizado) return { id: 'monitorado_com_captura', cor: '#1976D2', texto: 'Triatomíneo Capturado — Análise Lab. Pendente' };
    if (temCaptura && laudoFinalizado) return { id: 'negativo', cor: '#388E3C', texto: 'T. cruzi Negativo' };
    if (!temCaptura && temVestigios) return { id: 'vestigios', cor: '#FBC02D', texto: 'Vestígios Presentes' };
    return { id: 'sem_captura', cor: '#546E7A', texto: 'Pesquisado — Sem Triatomíneo' };
};

// ── Utilities ─────────────────────────────────────────────────────────────────
const formatDateUTC = (dateString, options) => {
    if (!dateString) return 'N/A';
    if (dateString.length === 10 && dateString.includes('-')) return new Date(`${dateString}T00:00:00.000Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC', ...options });
    return new Date(dateString).toLocaleString('pt-BR', { timeZone: 'UTC', ...options });
};

const parseFotos = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field.filter(Boolean);
    if (typeof field === 'string') {
        const t = field.trim();
        if (t.startsWith('{') && t.endsWith('}')) return t.slice(1, -1).split(',').map(s => s.trim().replace(/^"|"$/g, '')).filter(Boolean);
        try { const p = JSON.parse(t); if (Array.isArray(p)) return p.filter(Boolean); } catch {}
    }
    return [];
};

const toThumb = (url, width = 400) => {
    if (!url || !url.includes('/storage/v1/object/public/')) return url;
    return url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/') + `?width=${width}&quality=65`;
};

// ── Map type layers ───────────────────────────────────────────────────────────
const MAP_TYPES = [
    { id: 'roadmap', name: 'Google Maps' },
    { id: 'hybrid',  name: 'Google Satélite' },
];

// ── Address Search ────────────────────────────────────────────────────────────
const AddressSearchBar = ({ mapRef, onClose, onSearchSuccess }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const autocompleteRef = useRef(null);
    const geocoderRef = useRef(null);

    useEffect(() => {
        if (window.google?.maps?.places) {
            autocompleteRef.current = new window.google.maps.places.AutocompleteService();
            geocoderRef.current = new window.google.maps.Geocoder();
        }
    }, []);

    const handleSelectSuggestion = (item) => {
        setSearchQuery(item.description);
        setSuggestions([]);

        if (item._nominatim) {
            const n = item._nominatim;
            const lat = parseFloat(n.lat), lon = parseFloat(n.lon);
            if (isNaN(lat) || isNaN(lon)) return;
            if (n.boundingbox && mapRef.current) {
                const [minLat, maxLat, minLon, maxLon] = n.boundingbox.map(parseFloat);
                mapRef.current.fitBounds({ south: minLat, north: maxLat, west: minLon, east: maxLon });
            } else { mapRef.current?.panTo({ lat, lng: lon }); mapRef.current?.setZoom(17); }
            onSearchSuccess(); onClose(); return;
        }

        geocoderRef.current?.geocode({ placeId: item.place_id }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const vp = results[0].geometry.viewport;
                if (vp && mapRef.current) mapRef.current.fitBounds(vp);
                else { const loc = results[0].geometry.location; mapRef.current?.panTo({ lat: loc.lat(), lng: loc.lng() }); mapRef.current?.setZoom(17); }
                onSearchSuccess(); onClose();
            }
        });
    };

    useEffect(() => {
        if (searchQuery.length < 3) { setSuggestions([]); return; }
        const timer = setTimeout(async () => {
            setIsLoading(true);
            const tryNominatim = async () => {
                try {
                    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=br&limit=6&addressdetails=1`, { headers: { 'Accept-Language': 'pt-BR' } });
                    const data = await res.json();
                    setSuggestions((Array.isArray(data) ? data : []).map(d => ({ place_id: d.place_id, description: d.display_name, _nominatim: d })));
                } catch { setSuggestions([]); } finally { setIsLoading(false); }
            };
            if (autocompleteRef.current) {
                autocompleteRef.current.getPlacePredictions({ input: searchQuery, componentRestrictions: { country: 'br' } }, (predictions, status) => {
                    const ok = window.google.maps.places.PlacesServiceStatus.OK;
                    if (status === ok && predictions?.length > 0) { setIsLoading(false); setSuggestions(predictions); }
                    else tryNominatim();
                });
            } else tryNominatim();
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    return (
        <div className="address-search-bar">
            <div className="submenu-header"><h3>Pesquisar</h3><button onClick={onClose} className="close-submenu-btn">{ICONS.close}</button></div>
            <div className="address-search-body">
                <div className="search-input-wrapper">{ICONS.search}<input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar endereço ou cidade..." autoFocus /></div>
                {isLoading && <div className="loading-indicator">Buscando...</div>}
                {suggestions.length > 0 && <ul className="suggestions-list">{suggestions.map(item => <li key={item.place_id} onClick={() => handleSelectSuggestion(item)}>{item.description}</li>)}</ul>}
            </div>
        </div>
    );
};

// ── Filter module ─────────────────────────────────────────────────────────────
const FilterModule = ({ filtros, setFiltros }) => {
    const handleFilterChange = (e) => { const { name, value } = e.target; setFiltros(prev => ({ ...prev, [name]: value })); };
    return (
        <div className="filter-module">
            <div className="filter-row"><div className="filter-group full-width"><label htmlFor="filtroTipo">ORIGEM</label>
                <select id="filtroTipo" name="filtroTipo" value={filtros.filtroTipo} onChange={handleFilterChange}>
                    <option value="todos">Todas as Origens</option><option value="ocorrencias">Vigilância Ativa</option><option value="pits">Vigilância Passiva</option>
                </select></div></div>
            <div className="filter-row"><div className="filter-group full-width"><label htmlFor="filtroStatus">CLASSIFICAÇÃO</label>
                <select id="filtroStatus" name="filtroStatus" value={filtros.filtroStatus} onChange={handleFilterChange}>
                    <option value="todos">Todas</option>
                    <option value="denuncia_pendente">Notificação Pendente</option>
                    <option value="denuncia_atendida">Notificação Atendida</option>
                    <option value="denuncia_recusada">Notificação Improcedente</option>
                    <option value="critico">T. cruzi Positivo</option>
                    <option value="monitorado_com_captura">Triatomíneo Capturado — Análise Lab. Pendente</option>
                    <option value="negativo">T. cruzi Negativo</option>
                    <option value="vestigios">Vestígios Presentes</option>
                    <option value="sem_captura">Pesquisado — Sem Triatomíneo</option>
                </select></div></div>
            <div className="filter-row"><div className="filter-group full-width"><label>PERÍODO</label>
                <div className="date-inputs-compact">
                    <input type="date" id="dataInicio" name="dataInicio" value={filtros.dataInicio} onChange={handleFilterChange} />
                    <span>até</span>
                    <input type="date" id="dataFim" name="dataFim" value={filtros.dataFim} onChange={handleFilterChange} />
                </div></div></div>
        </div>
    );
};

// ── List item ────────────────────────────────────────────────────────────────
// React.memo: não re-renderiza se item e onSelectItem não mudaram.
// onSelectItem deve ser a função estável handleSelectItem (não uma arrow inline).
const FocusListItem = React.memo(({ item, onSelectItem }) => {
    const { dados, classificacao } = item;
    const endereco = dados.ocorrencia?.endereco || dados.denuncia?.rua || 'Endereço não informado';
    const numero = dados.ocorrencia?.numero || dados.denuncia?.numero || 'S/N';
    const data = dados.ocorrencia?.data_atividade || dados.denuncia?.created_at;
    const lat = parseFloat(dados.ocorrencia?.latitude || dados.denuncia?.latitude);
    const lng = parseFloat(dados.ocorrencia?.longitude || dados.denuncia?.longitude);
    const semGps = isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0);
    const handleClick = React.useCallback(() => onSelectItem(item), [item, onSelectItem]);
    return (
        <div className="focus-list-item" onClick={handleClick} style={{ '--status-color': classificacao.cor }}>
            <div className='item-content'>
                <span className="item-status-text">{classificacao.texto}{semGps && <span className="no-gps-badge" title="Sem coordenadas GPS"> · sem GPS</span>}</span>
                <span className="item-address">{`${endereco}, ${numero}`}</span>
                <span className="item-date">{formatDateUTC(data, { dateStyle: 'short' })}</span>
            </div>
        </div>
    );
});

// ── Detail helpers ────────────────────────────────────────────────────────────
const NI = ({ children }) => children ? <span>{children}</span> : <em style={{ color: '#94a3b8' }}>Não informado</em>;
const DetailRow = ({ label, value }) => <li><strong>{label}:</strong> <NI>{value}</NI></li>;

// ── Detail panel ──────────────────────────────────────────────────────────────
const FocusDetailPanel = ({ item, onBack }) => {
    const { dados, classificacao } = item;
    const { denuncia, ocorrencia, exame } = dados;
    const formatDate = (d) => formatDateUTC(d, { dateStyle: 'short', timeStyle: 'short' });
    const formatDateOnly = (d) => formatDateUTC(d, { dateStyle: 'short' });

    const formatActivities = (activities) => {
        if (!activities) return null;
        let list = [];
        if (Array.isArray(activities)) list = activities;
        else if (typeof activities === 'string') {
            const t = activities.trim();
            if (t.startsWith('{') && t.endsWith('}')) list = t.slice(1, -1).split(',').map(s => s.trim().replace(/^"|"$/g, '')).filter(Boolean);
            else { try { const p = JSON.parse(t); list = Array.isArray(p) ? p : [t]; } catch { list = t.split(',').map(s => s.trim()).filter(Boolean); } }
        }
        if (list.length === 0) return null;
        return list.map(a => { const v = String(a).toLowerCase(); if (v.includes('pesquisa')) return 'Pesquisa'; if (v.includes('borrifacao') || v.includes('borrifação')) return 'Borrifação'; if (v.includes('pit')) return 'Atendimento ao PIT'; return a.charAt(0).toUpperCase() + a.slice(1); }).join(', ');
    };

    const formatPendencia = (t) => { if (!t) return null; const l = t.toLowerCase(); if (l === 'sempendencias') return 'Sem Pendências'; if (l === 'fechado') return 'Domicílio Fechado'; if (l === 'recusa') return 'Recusa'; return t; };
    const formatMelhoria = (v) => { if (v === true) return 'Sim'; if (v === false) return 'Não'; return null; };
    const MATERIAL_MAP = { alvenaria_reboco: 'Alvenaria c/ reboco', alvenaria_sem_reboco: 'Alvenaria s/ reboco', barro_reboco: 'Barro c/ reboco', barro_sem_reboco: 'Barro s/ reboco', madeira: 'Madeira', taipa: 'Taipa', palha: 'Palha', telha: 'Telha', metalico: 'Metálico', outros: 'Outros', nova: 'Nova (primeira pesquisa)', reconhecida: 'Reconhecida', demolida: 'Demolida' };
    const formatMaterial = (v) => (v && MATERIAL_MAP[v]) ? MATERIAL_MAP[v] : (v || null);

    const fotoDenuncia = denuncia?.foto_url || null;
    const fotosAtendimento = parseFotos(ocorrencia?.fotos_urls).filter(u => u !== fotoDenuncia);
    const lat = parseFloat(ocorrencia?.latitude || denuncia?.latitude);
    const lng = parseFloat(ocorrencia?.longitude || denuncia?.longitude);
    const semCoordenadas = isNaN(lat) || isNaN(lng);

    return (
        <div className="focus-detail-panel">
            <div className="detail-header">
                <button onClick={onBack} className="back-button">&larr; Voltar para a lista</button>
                <div className="detail-title" style={{ '--status-color': classificacao.cor }}>
                    <span className="detail-status-text">{classificacao.texto}</span>
                    <h3 className="detail-address">{denuncia?.bairro || ocorrencia?.localidade_details?.nome || ocorrencia?.nome_localidade || 'Localidade não informada'}</h3>
                </div>
            </div>
            {semCoordenadas && <div className="no-coords-banner">📍 GPS não registrado — localização aproximada não disponível.</div>}
            <div className="detail-content">
                {denuncia && (
                    <div className="details-section">
                        <h4 className="section-title">Denúncia</h4>
                        {fotoDenuncia && <div className="denuncia-foto-wrapper"><a href={fotoDenuncia} target="_blank" rel="noopener noreferrer"><img src={toThumb(fotoDenuncia, 600)} alt="Foto da denúncia" className="denuncia-foto-principal" loading="lazy" decoding="async" /><span className="denuncia-foto-label">🔍 Ampliar</span></a></div>}
                        <ul className="details-list">
                            <li style={{ fontFamily: 'monospace', fontSize: '11px', color: '#94a3b8' }}>ID: {denuncia.id}</li>
                            <DetailRow label="Data / Hora" value={formatDate(denuncia.created_at)} />
                            <DetailRow label="Status" value={denuncia.status} />
                            <DetailRow label="Endereço" value={[denuncia.rua, denuncia.numero].filter(Boolean).join(', ')} />
                            <DetailRow label="Bairro" value={denuncia.bairro} />
                            <DetailRow label="Complemento" value={denuncia.complemento} />
                            <DetailRow label="Descrição" value={denuncia.descricao} />
                            {denuncia.feedback_agente && <li style={{ marginTop: 8, padding: '10px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', listStyle: 'none' }}><strong style={{ color: '#0369a1' }}>Feedback ao Cidadão:</strong><p style={{ margin: '4px 0 0', fontStyle: 'italic', color: '#0369a1' }}>"{denuncia.feedback_agente}"</p></li>}
                        </ul>
                    </div>
                )}
                {ocorrencia && (
                    <div className="details-section">
                        <h4 className="section-title">🔬 Visita Técnica do Agente</h4>
                        <h5 className="subsection-title">Identificação</h5>
                        <ul className="details-list">
                            <DetailRow label="Agente" value={ocorrencia.agente_details?.nome || ocorrencia.nome_agente_view} />
                            <DetailRow label="Data da Visita" value={formatDateOnly(ocorrencia.data_atividade)} />
                            <DetailRow label="Tipo de Atividade" value={formatActivities(ocorrencia.tipo_atividade)} />
                            <DetailRow label="Nº do PIT" value={ocorrencia.numero_pit} />
                            <DetailRow label="Código de Etiqueta" value={ocorrencia.codigo_etiqueta} />
                        </ul>
                        <h5 className="subsection-title">Domicílio</h5>
                        <ul className="details-list">
                            <DetailRow label="Localidade" value={ocorrencia.localidade_details?.nome || ocorrencia.nome_localidade} />
                            <DetailRow label="Cód. Localidade" value={ocorrencia.codigo_localidade} />
                            <DetailRow label="Categoria" value={ocorrencia.categoria_localidade || ocorrencia.localidade_details?.categoria} />
                            <DetailRow label="Endereço" value={ocorrencia.endereco} />
                            <DetailRow label="Número" value={ocorrencia.numero} />
                            <DetailRow label="Complemento" value={ocorrencia.complemento} />
                            <DetailRow label="Nome do Morador" value={ocorrencia.nome_morador} />
                            <DetailRow label="Qtd. Moradores" value={ocorrencia.quantidade_moradores != null ? String(ocorrencia.quantidade_moradores) : null} />
                            <DetailRow label="Nº de Anexos" value={ocorrencia.numero_anexo != null ? String(ocorrencia.numero_anexo) : null} />
                        </ul>
                        <h5 className="subsection-title">Estrutura do Imóvel</h5>
                        <ul className="details-list-grid">
                            <DetailRow label="Situação" value={formatMaterial(ocorrencia.situacao_imovel)} />
                            <DetailRow label="Tipo de Parede" value={formatMaterial(ocorrencia.tipo_parede)} />
                            <DetailRow label="Tipo de Teto" value={formatMaterial(ocorrencia.tipo_teto)} />
                            <DetailRow label="Melhoria Habitacional" value={formatMelhoria(ocorrencia.melhoria_habitacional)} />
                            <DetailRow label="Pendência Pesquisa" value={formatPendencia(ocorrencia.pendencia_pesquisa)} />
                            <DetailRow label="Pendência Borrifação" value={formatPendencia(ocorrencia.pendencia_borrifacao)} />
                        </ul>
                        <h5 className="subsection-title">Captura de Triatomíneos</h5>
                        <ul className="details-list-grid">
                            <DetailRow label="Vestígios (Intra)" value={ocorrencia.vestigios_intradomicilio} />
                            <DetailRow label="Barbeiros (Intra)" value={ocorrencia.barbeiros_intradomicilio != null ? String(ocorrencia.barbeiros_intradomicilio) : null} />
                            <DetailRow label="Vestígios (Peri)" value={ocorrencia.vestigios_peridomicilio} />
                            <DetailRow label="Barbeiros (Peri)" value={ocorrencia.barbeiros_peridomicilio != null ? String(ocorrencia.barbeiros_peridomicilio) : null} />
                        </ul>
                        <h5 className="subsection-title">Borrifação</h5>
                        <ul className="details-list-grid">
                            <DetailRow label="Inseticida" value={ocorrencia.inseticida} />
                            <DetailRow label="Nº de Cargas" value={ocorrencia.numero_cargas != null ? String(ocorrencia.numero_cargas) : null} />
                        </ul>
                        <h5 className="subsection-title">Registros do Atendimento</h5>
                        {fotosAtendimento.length > 0
                            ? <div className="gallery-container">{fotosAtendimento.map((url, i) => <div key={i} className="gallery-thumbnail-wrapper"><a href={url} target="_blank" rel="noopener noreferrer"><img src={toThumb(url, 300)} alt={`Foto ${i + 1}`} className="gallery-thumbnail" loading="lazy" decoding="async" /></a></div>)}</div>
                            : <p className="no-data-text">Nenhuma foto registrada pelo agente.</p>}
                    </div>
                )}
                {exame && (
                    <div className="details-section">
                        <h4 className="section-title">🧫 Análise Laboratorial</h4>
                        <ul className="details-list">
                            <DetailRow label="Técnico Responsável" value={exame.tecnico_responsavel} />
                            <DetailRow label="Data do Exame" value={formatDateOnly(exame.data_exame)} />
                            <DetailRow label="Situação" value={exame.situacao} />
                        </ul>
                        <h5 className="subsection-title">Exemplares Examinados ({exame.exames_exemplares?.length || 0})</h5>
                        <div className="exemplares-list">
                            {exame.exames_exemplares?.length > 0 ? exame.exames_exemplares.map((ex, idx) => {
                                const isPositivo = ex.resultado?.toLowerCase().includes('positivo');
                                return (
                                    <div key={ex.id || idx} className="exemplar-item">
                                        <div className="exemplar-grid"><div><strong>Vetor:</strong> {ex.vetor || 'N/A'}</div><div><strong>Estágio:</strong> {ex.estagio || 'N/A'}</div><div><strong>Captura:</strong> {ex.captura || 'N/A'}</div></div>
                                        <div className="exemplar-resultado"><strong>Resultado:</strong><span style={{ fontWeight: 'bold', color: isPositivo ? '#B91C1C' : '#374151' }}>{ex.resultado || 'N/A'}</span></div>
                                    </div>
                                );
                            }) : <p className="no-data-text">Nenhum exemplar examinado.</p>}
                        </div>
                    </div>
                )}
                {!denuncia && !ocorrencia && <p className="no-data-text" style={{ padding: 20 }}>Sem dados disponíveis para este ponto.</p>}
            </div>
        </div>
    );
};

// ── Hover preview ─────────────────────────────────────────────────────────────
const CARD_W = 280;
const CARD_H = 210; // imagem 160px + info ~50px
const HOVER_OFFSET = 14;

// Sempre montado no DOM (visibility:hidden quando sem item) para que o ref
// esteja disponível antes do primeiro hover — posição controlada por transform
// diretamente no DOM, sem passar pelo estado do React.
const HoverPreviewCard = React.forwardRef(({ item }, ref) => {
    const { dados, classificacao } = item || {};
    const fotos = dados ? parseFotos(dados.ocorrencia?.fotos_urls) : [];
    const fotoUrl = dados ? (dados.denuncia?.foto_url || fotos[0] || null) : null;
    return (
        <div ref={ref} className="hover-preview-card" style={{ visibility: item ? 'visible' : 'hidden' }}>
            {fotoUrl
                ? <img src={toThumb(fotoUrl, 200)} alt={classificacao?.texto} className="hover-preview-image" loading="lazy" decoding="async" />
                : <div className="hover-preview-no-image">Sem foto</div>}
            <div className="hover-preview-info">
                {classificacao && <span className="hover-preview-status" style={{ backgroundColor: classificacao.cor }}>{classificacao.texto}</span>}
            </div>
        </div>
    );
});

// ── Main component ────────────────────────────────────────────────────────────
const LIBRARIES = ['places'];

const OperationalMap = ({ registros: allRegistros = [], pits: allPits = [], exames: allExames = [], municipioInfo, allAgents = [], allLocalities = [] }) => {
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: LIBRARIES,
        language: 'pt-BR',
    });

    const { width } = useWindowSize();
    const isMobile = width <= 768;
    const mapRef = useRef(null);
    const panelRef = useRef(null);
    const markersRef = useRef([]);
    const initialFitDone = useRef(false);
    const isDraggingRef = useRef(false);
    const mapWrapperRef = useRef(null);
    const mouseMoveRafRef = useRef(null);
    const hoverCardRef = useRef(null);
    const hoveredItemRef = useRef(null);
    const mapAnimationListenerRef = useRef(null);

    const [isMapReady, setIsMapReady] = useState(false);

    const [filtros, setFiltros] = useState({ filtroTipo: 'todos', filtroStatus: 'todos', dataInicio: '', dataFim: '' });
    const [selectedItem, setSelectedItem] = useState(null);
    const [activeLayer, setActiveLayer] = useState('hybrid');
    const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [activePanel, setActivePanel] = useState(null);

    // Ref síncrono com o estado — lido dentro de callbacks sem re-criar a closure
    hoveredItemRef.current = hoveredItem;

    useEffect(() => {
        window.dispatchEvent(new CustomEvent('sidebar-collapse-request', { detail: { collapse: true } }));
        return () => window.dispatchEvent(new CustomEvent('sidebar-collapse-request', { detail: { collapse: false } }));
    }, []);

    useEffect(() => { if (!isMobile) setIsMobilePanelOpen(false); }, [isMobile]);

    const pontosFiltrados = useMemo(() => {
        const { filtroTipo, filtroStatus, dataInicio, dataFim } = filtros;
        const dataInicioObj = dataInicio ? new Date(`${dataInicio}T00:00:00.000Z`) : null;
        const dataFimObj = dataFim ? new Date(`${dataFim}T23:59:59.999Z`) : null;

        const getFilterDate = (dateStr) => {
            if (!dateStr) return null;
            return dateStr.length === 10 && dateStr.includes('-') ? new Date(`${dateStr}T00:00:00.000Z`) : new Date(dateStr);
        };
        const isInDateRange = (dateStr) => {
            // Sem filtro de período ativo → todos os itens passam, inclusive os sem data
            if (!dataInicioObj && !dataFimObj) return true;
            if (!dateStr) return false;
            const d = getFilterDate(dateStr);
            if (!d) return false;
            return (dataInicioObj ? d >= dataInicioObj : true) && (dataFimObj ? d <= dataFimObj : true);
        };

        const pitsById = new Map(allPits.map(p => [p.id, p]));
        const agentsById = new Map(allAgents.map(a => [a.id, a]));
        const localitiesById = new Map(allLocalities.map(l => [l.id, l]));
        const examesByOcorrenciaId = new Map(allExames.map(e => [e.ocorrencia_id, e]));

        const pontos = [];
        const denunciasAtendidasNoPeriodo = new Set();

        allRegistros.forEach(reg => {
            if (!isInDateRange(reg.data_atividade)) return;
            const denunciaOriginal = pitsById.get(reg.denuncia_id) || null;
            if (denunciaOriginal) denunciasAtendidasNoPeriodo.add(denunciaOriginal.id);
            const item = {
                id: `ocorrencia-${reg.id}`,
                tipo: 'ocorrencia',
                dados: {
                    ocorrencia: { ...reg, agente_details: agentsById.get(reg.agente_id) || null, localidade_details: localitiesById.get(reg.localidade_id) || null },
                    denuncia: denunciaOriginal,
                    exame: examesByOcorrenciaId.get(reg.id) || null,
                },
            };
            item.classificacao = getClassificacaoFoco(item);
            pontos.push(item);
        });

        allPits.forEach(pit => {
            if (!isInDateRange(pit.created_at)) return;
            if (denunciasAtendidasNoPeriodo.has(pit.id)) return;
            const item = {
                id: `denuncia-${pit.id}`,
                tipo: 'denuncia',
                dados: { denuncia: { ...pit, localidade_details: localitiesById.get(pit.localidade_id) || null }, ocorrencia: null, exame: null },
            };
            item.classificacao = getClassificacaoFoco(item);
            pontos.push(item);
        });

        return pontos.filter(p => {
            const passaTipo = filtroTipo === 'todos'
                || (filtroTipo === 'ocorrencias' && p.tipo === 'ocorrencia' && !p.dados.denuncia)
                || (filtroTipo === 'pits' && (p.tipo === 'denuncia' || !!p.dados.denuncia));
            // "Denúncia Atendida" = PIT que já teve visita do agente (tem ocorrência vinculada)
            // Os pins aparecem com a cor do resultado da visita (sem_captura, negativo, etc.)
            const passaStatus = filtroStatus === 'todos'
                || (filtroStatus === 'denuncia_atendida'
                    ? (p.classificacao.id === 'denuncia_atendida' || (!!p.dados.denuncia && !!p.dados.ocorrencia))
                    : p.classificacao.id === filtroStatus);
            return passaTipo && passaStatus;
        }).sort((a, b) => {
            const da = a.dados.ocorrencia?.data_atividade || a.dados.denuncia?.created_at;
            const db = b.dados.ocorrencia?.data_atividade || b.dados.denuncia?.created_at;
            return (!da || !db) ? 0 : new Date(db) - new Date(da);
        });
    }, [allRegistros, allPits, allExames, filtros, allAgents, allLocalities]);

    useEffect(() => {
        if (selectedItem && !pontosFiltrados.find(p => p.id === selectedItem.id)) setSelectedItem(null);
    }, [pontosFiltrados, selectedItem]);

    const pontosFiltradosRef = useRef([]);
    pontosFiltradosRef.current = pontosFiltrados;

    const pontosComGps = useMemo(() =>
        pontosFiltrados.filter(p => {
            const lat = parseFloat(p.dados.ocorrencia?.latitude || p.dados.denuncia?.latitude);
            const lng = parseFloat(p.dados.ocorrencia?.longitude || p.dados.denuncia?.longitude);
            return !isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0);
        }), [pontosFiltrados]);

    // initialFitDone = true somente APÓS o fit nos pins reais ser concluído.
    // O Nominatim checa esse ref antes de aplicar qualquer fit, evitando sobrescrever os pins.

    const fitAllBounds = useCallback(() => {
        if (!mapRef.current) return false;
        const points = pontosFiltradosRef.current;
        const bounds = new window.google.maps.LatLngBounds();
        let count = 0;
        points.forEach(p => {
            const lat = parseFloat(p.dados.ocorrencia?.latitude || p.dados.denuncia?.latitude);
            const lng = parseFloat(p.dados.ocorrencia?.longitude || p.dados.denuncia?.longitude);
            if (!isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0)) {
                bounds.extend({ lat, lng });
                count++;
            }
        });
        if (count === 0) return false;
        isDraggingRef.current = true;
        setHoveredItem(null);
        // Limpa listener anterior para evitar acúmulo se fitAllBounds for chamado rápido
        if (mapAnimationListenerRef.current) {
            window.google.maps.event.removeListener(mapAnimationListenerRef.current);
        }
        const panelW = isMobile ? 0 : (panelRef.current?.offsetWidth || 0);
        mapRef.current.fitBounds(bounds, { left: panelW + 24, top: 60, right: 60, bottom: 60 });
        // Failsafe: libera a trava após 1s caso o idle demore a disparar
        const failsafe = setTimeout(() => { isDraggingRef.current = false; }, 1000);
        mapAnimationListenerRef.current = mapRef.current.addListener('idle', () => {
            clearTimeout(failsafe);
            isDraggingRef.current = false;
            window.google.maps.event.removeListener(mapAnimationListenerRef.current);
            mapAnimationListenerRef.current = null;
        });
        return true;
    }, [isMobile]);

    // Centralização inicial com debounce: aguarda os dados estabilizarem
    // antes de fazer o fit, evitando centralizar em lotes parciais.
    useEffect(() => {
        if (!isMapReady || initialFitDone.current || pontosComGps.length === 0) return;

        const timer = setTimeout(() => {
            if (initialFitDone.current) return;
            initialFitDone.current = true;
            fitAllBounds();
        }, 500);

        return () => clearTimeout(timer);
    }, [isMapReady, pontosComGps, fitAllBounds]);

    // Quando o usuário muda um filtro (após o fit inicial), o mapa
    // ajusta automaticamente a visão para mostrar os pins filtrados.
    const filterFitSkipFirst = useRef(true);
    useEffect(() => {
        if (filterFitSkipFirst.current) { filterFitSkipFirst.current = false; return; }
        if (!isMapReady) return;
        fitAllBounds();
    }, [filtros, isMapReady, fitAllBounds]);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        // Rastreia drag para pausar re-renders de hover durante o pan
        map.addListener('dragstart', () => { isDraggingRef.current = true; setHoveredItem(null); });
        map.addListener('dragend',   () => { isDraggingRef.current = false; });
        setIsMapReady(true);
    }, []);

    // ResizeObserver: quando o container do mapa muda de tamanho (ex: sidebar colapsando),
    // dispara o evento de resize interno do Google Maps para ele redesenhar os tiles corretamente.
    useEffect(() => {
        if (!isMapReady || !mapRef.current) return;
        const mapDiv = mapRef.current.getDiv();
        if (!mapDiv || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            if (mapRef.current) window.google.maps.event.trigger(mapRef.current, 'resize');
        });
        ro.observe(mapDiv);
        return () => ro.disconnect();
    }, [isMapReady]);

    const handleSelectItem = useCallback((item) => {
        // Anima o mapa primeiro (alta prioridade), React monta o painel depois
        if (mapRef.current) {
            const lat = parseFloat(item.dados.ocorrencia?.latitude || item.dados.denuncia?.latitude);
            const lng = parseFloat(item.dados.ocorrencia?.longitude || item.dados.denuncia?.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                mapRef.current.panTo({ lat, lng });
                mapRef.current.setZoom(19);
                if (!isMobile) {
                    const panelW = panelRef.current?.offsetWidth || 0;
                    if (panelW > 0) mapRef.current.panBy(-panelW / 2, 0);
                }
            }
        }
        // startTransition: marca como baixa prioridade — não bloqueia o frame do Maps
        startTransition(() => {
            setSelectedItem(item);
            setActivePanel(null);
            if (isMobile) setIsMobilePanelOpen(true);
        });
    }, [isMobile]);

    const handleClearSelection = useCallback(() => {
        startTransition(() => {
            setSelectedItem(null);
            if (isMobile) setIsMobilePanelOpen(false);
        });
    }, [isMobile]);

    // Quando o usuário volta para a lista (selectedItem vai a null),
    // aguarda 100ms para o React terminar de montar a lista antes de animar o mapa.
    const prevSelectedItem = useRef(null);
    useEffect(() => {
        if (prevSelectedItem.current && !selectedItem && isMapReady) {
            const t = setTimeout(() => fitAllBounds(), 100);
            return () => clearTimeout(t);
        }
        prevSelectedItem.current = selectedItem;
    }, [selectedItem, isMapReady, fitAllBounds]);

    const handleResetView = () => { setSelectedItem(null); fitAllBounds(); };
    const handleTogglePanel = (name) => { setActivePanel(cur => cur === name ? null : name); if (isMobile) setSelectedItem(null); };
    const handleLayerChange = (id) => { setActiveLayer(id); mapRef.current?.setMapTypeId(id); setActivePanel(null); };
    const handleGeolocate = () => navigator.geolocation.getCurrentPosition(
        pos => { mapRef.current?.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude }); mapRef.current?.setZoom(16); },
        () => alert('Não foi possível obter sua localização.')
    );
    const stableSetHoveredItem = useCallback(item => setHoveredItem(item), []);

    const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || undefined;
    const mapOptions = useMemo(() => ({
        disableDefaultUI: true, gestureHandling: 'greedy', clickableIcons: false, maxZoom: 22, tilt: 0,
        mapTypeId: 'hybrid',
        // Cor de fundo antes dos tiles carregarem — quase invisível no roadmap
        backgroundColor: '#e8e4dc',
        // Map ID habilita renderização vetorial (WebGL): elimina os cinzas de tiles raster.
        // Configure REACT_APP_GOOGLE_MAPS_MAP_ID no .env com um Map ID criado no Google Cloud Console.
        ...(mapId ? { mapId } : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);
    const mapCenter = useMemo(() => ({ lat: -10, lng: -50 }), []);
    const mapContainerStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

    // Markers gerenciados imperativamente — zero overhead de reconciliação React.
    // Recria só quando os dados ou o modo mobile mudam.
    useEffect(() => {
        if (!isMapReady || !mapRef.current) return;

        // Remove markers anteriores
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];

        pontosFiltrados.forEach(p => {
            const lat = parseFloat(p.dados.ocorrencia?.latitude || p.dados.denuncia?.latitude);
            const lng = parseFloat(p.dados.ocorrencia?.longitude || p.dados.denuncia?.longitude);
            if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;

            const marker = new window.google.maps.Marker({
                position: { lat, lng },
                map: mapRef.current,
                icon: getGoogleIcon(p.classificacao.id),
                zIndex: p.classificacao.id === 'critico' ? 1000 : undefined,
            });

            marker.addListener('click', () => handleSelectItem(p));
            if (!isMobile) {
                marker.addListener('mouseover', (e) => {
                    if (isDraggingRef.current) return;
                    // Posiciona o card imediatamente via DOM antes de torná-lo visível
                    if (hoverCardRef.current && mapWrapperRef.current) {
                        const r = mapWrapperRef.current.getBoundingClientRect();
                        const de = e.domEvent;
                        if (r && de) {
                            const x = de.clientX - r.left, y = de.clientY - r.top;
                            const flipX = r.width > 0 && x + HOVER_OFFSET + CARD_W > r.width;
                            const flipY = r.height > 0 && y + HOVER_OFFSET + CARD_H > r.height;
                            const tx = flipX ? x - HOVER_OFFSET - CARD_W : x + HOVER_OFFSET;
                            const ty = flipY ? y - HOVER_OFFSET - CARD_H : y + HOVER_OFFSET;
                            hoverCardRef.current.style.transform = `translate(${tx}px,${ty}px)`;
                        }
                    }
                    stableSetHoveredItem(p);
                });
                marker.addListener('mouseout', () => stableSetHoveredItem(null));
            }

            markersRef.current.push(marker);
        });

        return () => {
            markersRef.current.forEach(m => m.setMap(null));
            markersRef.current = [];
        };
    }, [isMapReady, pontosFiltrados, handleSelectItem, isMobile, stableSetHoveredItem]);

    // Listener nativo passivo: não bloqueia o compositor do browser durante pan/scroll
    useEffect(() => {
        const el = mapWrapperRef.current;
        if (!el) return;
        const handleNativeMouseMove = (e) => {
            if (isDraggingRef.current || !hoveredItemRef.current) return;
            if (mouseMoveRafRef.current) return;
            const r = el.getBoundingClientRect();
            const x = e.clientX - r.left, y = e.clientY - r.top;
            const w = r.width, h = r.height;
            mouseMoveRafRef.current = requestAnimationFrame(() => {
                if (hoverCardRef.current) {
                    const flipX = w > 0 && x + HOVER_OFFSET + CARD_W > w;
                    const flipY = h > 0 && y + HOVER_OFFSET + CARD_H > h;
                    const tx = flipX ? x - HOVER_OFFSET - CARD_W : x + HOVER_OFFSET;
                    const ty = flipY ? y - HOVER_OFFSET - CARD_H : y + HOVER_OFFSET;
                    hoverCardRef.current.style.transform = `translate3d(${tx}px,${ty}px,0)`;
                }
                mouseMoveRafRef.current = null;
            });
        };
        el.addEventListener('mousemove', handleNativeMouseMove, { passive: true });
        return () => el.removeEventListener('mousemove', handleNativeMouseMove);
    }, []);

    const containerClasses = ['operational-map-container', isMobile ? 'is-mobile' : '', isMobilePanelOpen ? 'mobile-panel-open' : ''].filter(Boolean).join(' ');

    if (!isLoaded) {
        return <div className={containerClasses}><div className="loading-screen"><div className="spinner" /><p>Carregando mapa...</p></div></div>;
    }

    let panelContent = null;
    if (activePanel === 'search') panelContent = <AddressSearchBar mapRef={mapRef} onClose={() => setActivePanel(null)} onSearchSuccess={() => {}} />;
    else if (activePanel === 'layers') panelContent = (
        <div className="toolbar-submenu">
            <div className="submenu-header"><h3>Camadas</h3><button onClick={() => setActivePanel(null)} className="close-submenu-btn">{ICONS.close}</button></div>
            <div className="submenu-options">{MAP_TYPES.map(t => <button key={t.id} className={`submenu-option ${activeLayer === t.id ? 'active' : ''}`} onClick={() => handleLayerChange(t.id)}>{t.name}</button>)}</div>
        </div>
    );
    else if (activePanel === 'filters') panelContent = (
        <div className="filter-panel">
            <div className="filter-panel-header"><h3>Filtros</h3><button onClick={() => setActivePanel(null)} className="close-submenu-btn">{ICONS.close}</button></div>
            <FilterModule filtros={filtros} setFiltros={setFiltros} allAgents={allAgents} allLocalities={allLocalities} />
        </div>
    );

    const floatingPanel = panelContent
        ? isMobile
            ? createPortal(<div className="floating-panel-backdrop" onClick={() => setActivePanel(null)}><div onClick={e => e.stopPropagation()}>{panelContent}</div></div>, document.body)
            : panelContent
        : null;

    return (
        <div className={containerClasses}>
            <aside className="situation-panel" ref={panelRef}>
                {/* Lista sempre no DOM — display:none evita remount de N itens no "Voltar" */}
                <div style={{ display: selectedItem ? 'none' : 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                    <div className="panel-header">
                        <div className="panel-header-content"><h2>Detalhes dos Registros</h2><p>Análise Geográfica de Focos</p></div>
                        {isMobile && <button onClick={() => setIsMobilePanelOpen(false)} className="close-panel-mobile">{ICONS.close}</button>}
                    </div>
                    <div className="panel-content">
                        <div className="list-divider">
                            <span>{pontosFiltrados.length} PONTOS ENCONTRADOS</span>
                            {pontosComGps.length < pontosFiltrados.length && (
                                <span className="gps-counter-warning" title={`${pontosFiltrados.length - pontosComGps.length} sem coordenadas GPS — não aparecem no mapa`}>
                                    📍 {pontosComGps.length}/{pontosFiltrados.length} com GPS
                                </span>
                            )}
                        </div>
                        <div className="focus-list-container">
                            {pontosFiltrados.length > 0
                                ? pontosFiltrados.map(p => <FocusListItem key={p.id} item={p} onSelectItem={handleSelectItem} />)
                                : <p className="no-results-text">Nenhum ponto encontrado para os filtros selecionados.</p>}
                        </div>
                    </div>
                </div>
                {selectedItem && <FocusDetailPanel item={selectedItem} onBack={handleClearSelection} />}
            </aside>

            <div className="map-wrapper" ref={mapWrapperRef}>
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={5}
                    center={mapCenter}
                    options={mapOptions}
                    onLoad={onMapLoad}
                />

                <div className="map-toolbar">
                    {isMobile && <button onClick={() => setIsMobilePanelOpen(true)} title="Mostrar Lista">{ICONS.list}</button>}
                    <button onClick={handleResetView} title="Visão geral dos focos">{ICONS.home}</button>
                    <button onClick={handleGeolocate} title="Minha localização">{ICONS.location}</button>
                    <button onClick={() => handleTogglePanel('layers')} title="Selecionar Camadas">{ICONS.layers}</button>
                    <button onClick={() => handleTogglePanel('search')} title="Buscar Endereço">{ICONS.search}</button>
                    <button onClick={() => handleTogglePanel('filters')} title="Filtros">{ICONS.filter}</button>
                </div>

                {floatingPanel}
                <HoverPreviewCard ref={hoverCardRef} item={hoveredItem} />
            </div>
        </div>
    );
};

export default OperationalMap;
